import { sortBy } from 'lodash';
import { FC } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';

import TeamBreadcrumbLink from '../../common/TeamBreadcrumbLink';
import { ExpirationLabels } from '../../../utils/constants';

import './UserCourseAssignments.scss';

interface IProps {
  user: IUser;
  teams?: ITeam[];
}

interface ICourseRowProps {
  userId: string;
  course: ICourse;
  courseAssignments?: ICourseAssignment[];
  teams?: ITeam[];
  onClick?: (a: ICourseAssignment) => void;
}

const getTeamExpirationDisplay = (team?: ITeam): string => {
  if (!team) {
    return ExpirationLabels.NA;
  }

  if (team.ignoreExpiration) {
    return ExpirationLabels.Indefinite;
  }

  if (team.expirationOverride) {
    return new Date(team.expirationOverride).toLocaleString();
  }

  return ExpirationLabels.NotSet;
};

const CourseRow: FC<ICourseRowProps> = ({ userId, course, courseAssignments, teams, onClick }) => {
  const { _id, teamId, title, isUnlocked } = course;
  const team = teams?.find((t) => t._id === teamId);
  const assignment = courseAssignments?.find(
    (a) => a.courseId === _id && ((!teamId && !a.teamId) || teamId === a.teamId),
  );

  const assigned = assignment?.assigned;
  const assignedValue = assigned ? new Date(assigned).toLocaleString() : undefined;
  const expiration = assignment?.expiration;
  const expirationValue = expiration
    ? new Date(expiration).toLocaleString()
    : ExpirationLabels.Indefinite;
  const teamExpirationOverrideValue = getTeamExpirationDisplay(team);
  const userExpirationOverride = assignment?.userExpirationOverride;
  const userExpirationOverrideValue = userExpirationOverride
    ? new Date(userExpirationOverride).toLocaleString()
    : ExpirationLabels.NotSet;

  return (
    <>
      <div>
        <Link to={`/admin/courses/${_id}`}>{title}</Link>
      </div>

      <div className="UserCourseAssignments-assigned">
        <div>{assignedValue}</div>
      </div>

      <div>
        <span>
          {team && teams ? (
            <TeamBreadcrumbLink
              team={team}
              teams={teams}
              to={`/admin/teams/${teamId}?userId=${userId}&tab=learners`}
              tooltipId={`assignment-team-${team._id}`}
            />
          ) : (
            'Direct'
          )}
        </span>
      </div>
      <div>
        <span>{expirationValue}</span>
      </div>
      <div>{teamExpirationOverrideValue}</div>
      <div>{userExpirationOverrideValue}</div>
      <div>{isUnlocked ? 'Unlocked' : 'Locked'}</div>
      <div>
        {!teamId && (
          <div
            onClick={() => {
              if (onClick && assignment) {
                onClick(assignment);
              }
            }}
          >
            <span className="material-icons">delete</span>
          </div>
        )}
      </div>
    </>
  );
};

const MergedAssignmentGrid: FC<IProps> = ({ user }) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const { mergedCourseAssignments, mergedCourses } = user;
  const assignedCourses = mergedCourses?.filter((c) => !c.isUnassigned);
  const renderRow = (course: ICourse) => {
    const { _id, title, isUnlocked } = course;
    const assignment = mergedCourseAssignments?.find((a) => a.courseId === _id);
    const assigned = assignment?.assigned;
    const assignedValue = assigned ? new Date(assigned).toLocaleString() : undefined;
    const expiration = assignment?.expiration;
    const expirationValue = expiration
      ? new Date(expiration).toLocaleString()
      : ExpirationLabels.Indefinite;
    const detailsUrl = `${location.pathname}?userId=${user._id}&courseId=${_id}${
      !!tab ? `&tab=${tab}` : ''
    }`;
    return (
      <>
        <div>
          <Link to={`/admin/courses/${_id}`}>{title}</Link>
        </div>
        <div>
          <div>{assignedValue}</div>
        </div>
        <div>
          <span>{expirationValue}</span>
        </div>
        <div>{isUnlocked ? 'Unlocked' : 'Locked'}</div>
        <div>
          <Link to={detailsUrl}>
            <span className="material-icons">settings</span>
          </Link>
        </div>
        <div className="assigned-border" />
        <div className="assigned-border" />
        <div className="assigned-border" />
        <div className="assigned-border" />
        <div className="assigned-border" />
      </>
    );
  };

  return (
    <div className="user-view-assigned-grid">
      <div>Course</div>
      <div>Assigned</div>
      <div>Expiration</div>
      <div>Navigation</div>
      <div />
      {sortBy(assignedCourses, 'title').map((c) => renderRow(c))}
    </div>
  );
};

const UserCourseAssignments: FC<
  IProps & {
    onAssignmentRemoved: (a: ICourseAssignment) => void;
  }
> = ({ user, teams, onAssignmentRemoved }) => {
  const { _id: userId, courses, courseAssignments } = user;
  const assignedCourses: ICourse[] = [];
  const unassignedCourses: ICourse[] = [];
  (courses as ICourse[])?.forEach((c) =>
    c.isUnassigned ? unassignedCourses.push(c) : assignedCourses.push(c),
  );
  const hasUnassigned = (unassignedCourses?.length ?? 0) > 0;
  return (
    <div className="UserCourseAssignments">
      <MergedAssignmentGrid user={user} teams={teams} />
      <div className="detailed-section">Detailed Assignment Breakdown</div>
      <div className="assigned-grid">
        <div>Course</div>
        <div>Assigned</div>
        <div>Direct/Team</div>
        <div>Default Expiration</div>
        <div>Team Override</div>
        <div>User Override</div>
        <div>Navigation</div>
        <div></div>
        {sortBy(assignedCourses, 'title').map((c) => {
          return (
            <>
              <CourseRow
                key={c._id}
                userId={userId as string}
                course={c}
                courseAssignments={courseAssignments}
                teams={teams}
                onClick={(assignment) => onAssignmentRemoved(assignment)}
              />
              <div className="assigned-border" />
              <div className="assigned-border" />
              <div className="assigned-border" />
              <div className="assigned-border" />
              <div className="assigned-border" />
              <div className="assigned-border" />
              <div className="assigned-border" />
              <div className="assigned-border" />
            </>
          );
        })}
      </div>
      <div className="unassigned">
        {hasUnassigned && (
          <>
            <div>Unassigned Team-level Courses</div>
            <div className="unassigned-grid">
              {sortBy(unassignedCourses, 'title').map((c) => {
                return (
                  <CourseRow
                    key={c._id}
                    userId={userId as string}
                    course={c}
                    courseAssignments={courseAssignments}
                    teams={teams}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UserCourseAssignments;
